import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Wrench, Check, CircleDashed, AlertTriangle, ExternalLink, Trash2, Newspaper, Gift, Accessibility } from 'lucide-react';
import api from '../lib/api';
import AccessableBulkLink from './AccessableBulkLink';
import EuansAmbiguousReview from './EuansAmbiguousReview';

const STEP_LABELS = {
  open_days: 'Populate opening days',
  regeocode: 'Re-geocode attractions from postcodes',
  link_health: 'Check every link is alive',
  tidy_text: 'Tidy legacy hard-wrapped descriptions',
  contact_junk: 'Repair migrated contact junk (recover website links, clean descriptions)',
  durable_images: 'Move uploaded images to durable storage (survives redeploys)',
  dwell_time: 'Estimate realistic visit times',
  accessibility: 'Refresh accessibility data (OSM, Changing Places, Euan\u2019s Guide, AccessAble)',
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [maintRun, setMaintRun] = useState(null);
  const [brokenLinks, setBrokenLinks] = useState([]);
  const [costSummary, setCostSummary] = useState(null);
  const [newsRun, setNewsRun] = useState(null);
  const [newsRunning, setNewsRunning] = useState(false);
  const [a11yRun, setA11yRun] = useState(null);
  const [a11yRunning, setA11yRunning] = useState(false);
  const [competition, setCompetition] = useState(null);
  const pollRef = useRef(null);

  function loadStats() {
    api.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
  }
  function loadMaint() {
    api.get('/admin/maintenance/runs/latest').then((r) => setMaintRun(r.data.run)).catch(() => {});
  }
  function loadBrokenLinks() {
    api.get('/admin/links/broken').then((r) => setBrokenLinks(r.data.items || [])).catch(() => {});
  }
  function loadCosts() {
    // Owner-only data — silently skipped for non-owner roles to avoid noisy 403s
    if ((localStorage.getItem('gl_admin_role') || 'admin') !== 'owner') return;
    api.get('/admin/costs/summary?weeks=4').then((r) => setCostSummary(r.data)).catch(() => {});
  }
  function loadNewsRun() {
    api.get('/admin/news-scrape/runs?limit=1').then((r) => setNewsRun((r.data.items || [])[0] || null)).catch(() => {});
  }
  function loadCompetition() {
    api.get('/admin/competition/current').then((r) => setCompetition(r.data)).catch(() => {});
  }
  useEffect(() => { loadStats(); loadMaint(); loadBrokenLinks(); loadCosts(); loadNewsRun(); loadCompetition(); }, []);

  // Poll every 2.5s while a maintenance run is active
  useEffect(() => {
    if (maintRun?.status === 'running') {
      pollRef.current = setInterval(loadMaint, 2500);
      return () => clearInterval(pollRef.current);
    }
    // Run just finished: refresh stats + broken links so the panels stay current
    if (maintRun?.status === 'complete' && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      loadStats();
      loadBrokenLinks();
    }
  }, [maintRun?.status]);

  async function startMaintenance() {
    const r = await api.post('/admin/maintenance/run');
    if (r.data.run) setMaintRun(r.data.run);
  }

  async function runNewsScrape() {
    if (newsRunning) return;
    setNewsRunning(true);
    try {
      const r = await api.post('/admin/news-scrape/run');
      setNewsRun(r.data);
      const matched = r.data?.items_matched || 0;
      const drafts = r.data?.drafts_created || 0;
      if (matched === 0) {
        alert('News scrape complete. Nothing new this run — try again next week.');
      } else {
        alert(`News scrape complete: ${matched} venue mention(s) found, ${drafts} draft(s) created. Check the Notifications inbox.`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'News scrape failed');
    } finally {
      setNewsRunning(false);
    }
  }

  async function runAccessibilityIngest() {
    if (a11yRunning) return;
    setA11yRunning(true);
    try {
      // Four sequential idempotent calls: Changing Places (small), OSM
      // (big-ish), Euan's Guide (sitemap match, ~55k urls), AccessAble
      // auto-link (instant). All safe to re-run.
      const cp = await api.post('/admin/accessibility/ingest-changing-places');
      const osm = await api.post('/admin/accessibility/ingest-osm');
      const euans = await api.post('/admin/accessibility/ingest-euans-guide');
      const acc = await api.post('/admin/accessibility/auto-link-accessable');
      const merged = {
        ran_at: new Date().toISOString(),
        cp_updated: cp.data.updated || 0,
        osm_updated: osm.data.updated || 0,
        euans_updated: euans.data.updated || 0,
        accessable_linked: acc.data.linked || 0,
      };
      setA11yRun(merged);
      alert(
        `Accessibility refresh complete:\n` +
        `• ${merged.osm_updated} venues updated from OpenStreetMap\n` +
        `• ${merged.cp_updated} from the Changing Places registry\n` +
        `• ${merged.euans_updated} matched to Euan's Guide reviews\n` +
        `• ${merged.accessable_linked} AccessAble search links added\n` +
        `(Manual venue-typed data was never overwritten.)`
      );
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Accessibility ingest failed');
    } finally {
      setA11yRunning(false);
    }
  }

  async function drawCompetitionWinner() {
    if (!competition || competition.entries === 0) {
      alert('No opted-in entries yet for this quarter.');
      return;
    }if (!window.confirm(`Pick a random winner from ${competition.entries} entries for ${competition.quarter}? This locks in the result (you can redraw later if needed).`)) return;
    try {
      const r = await api.post('/admin/competition/draw');
      alert(`Winner: ${r.data.winner_email}${r.data.winner_name ? ' (' + r.data.winner_name + ')' : ''}\n\nNow email them the StayPlayExplore voucher.`);
      loadCompetition();
    } catch (e) {
      alert(e.response?.data?.detail || 'Draw failed');
    }
  }

  async function redrawCompetitionWinner() {
    if (!window.confirm('Pick a NEW winner? The original is kept in the audit log. Use this only if the first winner didn\'t respond within 14 days.')) return;
    try {
      const r = await api.post('/admin/competition/redraw');
      alert(`New winner: ${r.data.winner_email}${r.data.winner_name ? ' (' + r.data.winner_name + ')' : ''}`);
      loadCompetition();
    } catch (e) {
      alert(e.response?.data?.detail || 'Redraw failed');
    }
  }

  async function deleteBroken(item) {
    const label = item.title || item.slug;
    if (!window.confirm(`Permanently delete ${item.kind} "${label}"? This removes it from the public site immediately.`)) return;
    try {
      const endpoint = item.kind === 'event'
        ? `/admin/events/${item.id || item.slug}`
        : `/admin/attractions/${item.slug}`;
      await api.delete(endpoint);
      // Drop locally so the UI reacts instantly; stats + count will refresh on next maintenance run
      setBrokenLinks((prev) => prev.filter((b) => (b.id || b.slug) !== (item.id || item.slug)));
      loadStats();
    } catch (err) {
      window.alert(`Couldn\u2019t delete: ${err.response?.data?.detail || err.message}`);
    }
  }

  async function ignoreBroken(item) {
    try {
      await api.post('/admin/links/ignore', { url: item.url, title: item.title || item.slug, kind: item.kind });
      setBrokenLinks((prev) => prev.filter((b) => b.url !== item.url));
    } catch (err) {
      window.alert(`Couldn\u2019t ignore: ${err.response?.data?.detail || err.message}`);
    }
  }

  const running = maintRun?.status === 'running';

  const tiles = stats ? [
    { label: 'Attractions', value: stats.published_attractions, total: stats.attractions, to: '/admin/attractions' },
    { label: 'No image', value: stats.attractions_needing_photo || 0, accent: (stats.attractions_needing_photo || 0) > 0, to: '/admin/attractions?filter=missing-photo' },
    { label: 'Poor quality photos', value: stats.attractions_poor_quality || 0, accent: (stats.attractions_poor_quality || 0) > 0, to: '/admin/attractions?filter=poor-quality' },
    { label: 'Missing opening days', value: stats.attractions_missing_open_days || 0, accent: (stats.attractions_missing_open_days || 0) > 0, to: '/admin/attractions?filter=missing-open-days' },
    { label: 'Missing postcode', value: stats.attractions_missing_postcode || 0, accent: (stats.attractions_missing_postcode || 0) > 0, to: '/admin/attractions?filter=missing-postcode' },
    { label: 'Dwell needs review', value: stats.attractions_dwell_needs_review || 0, accent: (stats.attractions_dwell_needs_review || 0) > 0, to: '/admin/attractions?filter=dwell-review' },
    { label: 'Open days needs review', value: stats.attractions_open_days_review || 0, accent: (stats.attractions_open_days_review || 0) > 0, to: '/admin/attractions?filter=open-days-review' },
    { label: 'Unpublished drafts', value: stats.attractions_unpublished || 0, accent: (stats.attractions_unpublished || 0) > 0, to: '/admin/attractions?filter=unpublished' },
    { label: 'Broken links', value: stats.broken_links_count || 0, accent: (stats.broken_links_count || 0) > 0, to: '#errors-to-fix' },
    ...(costSummary ? [{ label: 'This week (£)', value: `£${(costSummary.this_week_total_gbp || 0).toFixed(2)}`, to: '/admin/costs' }] : []),
    { label: 'Events pending', value: stats.events_pending, total: stats.events_approved + stats.events_pending, accent: stats.events_pending > 0, to: '/admin/events?status=pending' },
    { label: 'Awards pending', value: stats.awards_nominees_pending || 0, accent: (stats.awards_nominees_pending || 0) > 0, to: '/admin/awards' },
    { label: 'Finalists missing photo', value: stats.finalists_missing_image || 0, accent: (stats.finalists_missing_image || 0) > 0, to: '/admin/finalists' },
    { label: 'Listing edits', value: stats.listing_edits_pending || 0, accent: (stats.listing_edits_pending || 0) > 0, to: '/admin/attraction-edits' },
    { label: 'Reassurance flags', value: stats.sentinel_flags_pending || 0, accent: (stats.sentinel_flags_pending || 0) > 0, to: '/admin/sentinel' },
    { label: 'Members', value: stats.members, to: '/admin/members' },
    { label: 'Packages', value: stats.packages, to: '/admin/packages' },
    { label: 'Subscribers', value: stats.subscribers, to: '/admin/subscribers' },
    { label: 'Weekly digests', value: stats.digests, to: '/admin/digest' },
  ] : [];

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="label text-heritage mb-2">Overview</div>
          <h1 className="font-display text-4xl">What&apos;s happening on your site.</h1>
        </div>
        <button
          onClick={startMaintenance}
          disabled={running}
          data-testid="run-maintenance-btn"
          className="inline-flex items-center gap-2 bg-charcoal text-base px-5 py-3 rounded-full font-semibold hover:bg-charcoal/90 disabled:opacity-60 transition-colors"
        >
          <Wrench size={16}/>
          {running ? 'Healing\u2026' : 'Heal everything I can'}
        </button>
      </div>

      {maintRun && (
        <div className="mb-8 bg-white border border-charcoal/10 rounded-2xl p-6" data-testid="maintenance-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="label text-charcoal/50">Maintenance run</div>
              <div className="font-semibold">
                {maintRun.status === 'running' && 'Currently running…'}
                {maintRun.status === 'complete' && 'Last run completed successfully'}
                {maintRun.status === 'failed' && 'Last run failed — see error below'}
                {maintRun.status === 'interrupted' && 'Last run was interrupted (server restart) — run it again'}
              </div>
            </div>
            <div className="text-xs text-muted">
              {maintRun.started_at && new Date(maintRun.started_at).toLocaleString('en-GB')}
            </div>
          </div>
          <div className="space-y-3">
            {Object.keys(STEP_LABELS).map((stepKey) => {
              const step = maintRun.steps?.[stepKey] || {};
              const isCurrent = maintRun.current_step === stepKey;
              const done = !!step.result;
              const progress = step.progress;
              return (
                <div key={stepKey} className="flex items-center gap-3" data-testid={`maint-step-${stepKey}`}>
                  <div className="w-6 shrink-0">
                    {done ? <Check size={18} className="text-forest"/> : isCurrent ? <CircleDashed size={18} className="text-heritage animate-spin"/> : <CircleDashed size={18} className="text-charcoal/20"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{STEP_LABELS[stepKey]}</div>
                    {isCurrent && progress && (
                      <div className="text-xs text-muted truncate">{progress.i + 1} / {progress.total} · {progress.slug}</div>
                    )}
                    {done && (
                      <div className="text-xs text-muted">
                        {stepKey === 'open_days' && `Updated ${step.result.updated} of ${step.result.total} attractions`}
                        {stepKey === 'regeocode' && `Updated ${step.result.updated} · ${step.result.no_postcode} still missing postcodes`}
                        {stepKey === 'link_health' && `Checked ${step.result.checked ?? '?'} links · ${step.result.broken_count ?? step.result.broken?.length ?? 0} broken`}
                        {stepKey === 'tidy_text' && `Tidied ${step.result.updated} of ${step.result.checked} hard-wrapped descriptions`}
                        {stepKey === 'dwell_time' && `Estimated ${step.result.updated} dwell times · ${step.result.flagged_for_review || 0} flagged for review`}
                        {stepKey === 'accessibility' && (
                          <>
                            {`${step.result.osm_updated ?? 0} OSM · `}
                            {`${step.result.cp_updated ?? 0} Changing Places · `}
                            {`${step.result.euans_updated ?? 0} Euan\u2019s Guide · `}
                            {`${step.result.accessable_linked ?? 0} AccessAble auto-links`}
                            {step.result.euans_ambiguous ? ` · ${step.result.euans_ambiguous} need review` : ''}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {maintRun.status === 'failed' && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-xl p-3" data-testid="maint-error">
              {maintRun.error}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tiles.map((t) => {
          const isAnchor = t.to && t.to.startsWith('#');
          const Tag = isAnchor ? 'a' : Link;
          const linkProps = isAnchor ? { href: t.to } : { to: t.to };
          return (
            <Tag key={t.label} {...linkProps} data-testid={`stat-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`bg-white border rounded-2xl p-6 hover:shadow-soft transition-all hover:-translate-y-0.5 ${t.accent ? 'border-heritage' : 'border-charcoal/10'}`}>
              <div className="flex items-start justify-between">
                <div className="label text-charcoal/50">{t.label}</div>
                <ArrowUpRight size={16} className="text-charcoal/30"/>
              </div>
              <div className="font-display text-5xl mt-3">{t.value}</div>
              {t.total !== undefined && <div className="text-sm text-muted mt-1">of {t.total} total</div>}
            </Tag>
          );
        })}
      </div>

      {brokenLinks.length > 0 && (
        <div id="errors-to-fix" className="mt-12 bg-white border border-heritage rounded-2xl p-8" data-testid="errors-to-fix">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle size={20} className="text-heritage"/>
            <div className="label text-heritage">Errors that need fixing</div>
          </div>
          <h3 className="font-display text-2xl mb-2">{brokenLinks.length} broken {brokenLinks.length === 1 ? 'link' : 'links'} from the last health check</h3>
          <p className="text-sm text-muted mb-5">These URLs returned an error or timed out. Open the entry, fix the link (or remove it), then re-run a maintenance sweep.</p>
          <div className="space-y-2">
            {brokenLinks.map((b, i) => {
              const editTo = b.kind === 'event'
                ? `/admin/events?search=${encodeURIComponent(b.slug || b.title || '')}`
                : `/admin/attractions?search=${encodeURIComponent(b.slug || b.title || '')}`;
              return (
                <div key={i} className="flex items-start gap-3 bg-admin border border-charcoal/10 rounded-xl p-4" data-testid={`broken-link-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{b.title || b.slug}</span>
                      <span className="text-xs text-muted uppercase tracking-wider">{b.kind}</span>
                      {typeof b.status === 'number' && b.status > 0 && (
                        <span className="text-xs bg-heritage/15 text-heritage px-2 py-0.5 rounded font-semibold">HTTP {b.status}</span>
                      )}
                      {b.error && b.status === 0 && (
                        <span className="text-xs bg-heritage/15 text-heritage px-2 py-0.5 rounded font-semibold">{b.error.split(':')[0]}</span>
                      )}
                    </div>
                    <a href={b.url} target="_blank" rel="noreferrer noopener" className="text-xs text-muted hover:underline break-all flex items-center gap-1 mt-1">
                      <ExternalLink size={11}/> {b.url}
                    </a>
                  </div>
                  <Link
                    to={editTo}
                    className="text-xs font-semibold text-heritage hover:underline whitespace-nowrap shrink-0 mt-1"
                    data-testid={`broken-link-fix-${i}`}
                  >
                    Fix →
                  </Link>
                  <button
                    onClick={() => ignoreBroken(b)}
                    title="Stop flagging this URL (keeps the venue/event on the site)"
                    className="text-xs font-semibold text-charcoal/60 hover:bg-stone px-2 py-1 rounded-md whitespace-nowrap shrink-0 mt-1"
                    data-testid={`broken-link-ignore-${i}`}
                  >
                    Ignore
                  </button>
                  <button
                    onClick={() => deleteBroken(b)}
                    title="Delete this entry permanently"
                    className="text-xs font-semibold text-red-600 hover:bg-red-50 px-2 py-1 rounded-md whitespace-nowrap shrink-0 mt-1 inline-flex items-center gap-1"
                    data-testid={`broken-link-delete-${i}`}
                  >
                    <Trash2 size={12}/> Delete
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-charcoal/10 rounded-2xl p-8">
          <div className="label text-heritage mb-2">Quick actions</div>
          <h3 className="font-display text-2xl mb-6">Get something done.</h3>
          <div className="space-y-3">
            <Link to="/admin/digest" data-testid="quick-generate-digest" className="block bg-forest text-base px-5 py-4 rounded-xl font-semibold hover:bg-forest-deep transition-colors">
              Generate this week&apos;s AI digest →
            </Link>
            <Link to="/admin/events?status=pending" className="block bg-admin border border-charcoal/10 px-5 py-4 rounded-xl font-semibold hover:border-charcoal/30 transition-colors">
              Review {stats?.events_pending || 0} pending events →
            </Link>
            {(stats?.awards_nominees_pending || 0) > 0 && (
              <Link to="/admin/awards" data-testid="quick-approve-nominees" className="block bg-heritage text-base px-5 py-4 rounded-xl font-semibold hover:bg-heritage-deep transition-colors">
                Approve {stats.awards_nominees_pending} award {stats.awards_nominees_pending === 1 ? 'nomination' : 'nominations'} →
              </Link>
            )}
            <Link to="/admin/attractions" className="block bg-admin border border-charcoal/10 px-5 py-4 rounded-xl font-semibold hover:border-charcoal/30 transition-colors">
              Edit your attractions →
            </Link>
            <button
              onClick={runNewsScrape}
              disabled={newsRunning}
              data-testid="run-news-scrape"
              className="w-full text-left block bg-admin border border-charcoal/10 px-5 py-4 rounded-xl font-semibold hover:border-charcoal/30 transition-colors disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-2">
                <Newspaper size={16}/>
                {newsRunning ? 'Scanning local news…' : 'Scan local news for new venues now →'}
              </span>
              {newsRun && !newsRunning && (
                <span className="block text-xs text-muted mt-1 font-normal">
                  Last run {new Date(newsRun.started_at).toLocaleDateString('en-GB')} ·
                  {' '}{newsRun.items_seen || 0} articles ·
                  {' '}{newsRun.items_matched || 0} matched ·
                  {' '}{newsRun.drafts_created || 0} drafts created.
                  Auto-runs every Sunday at 06:00.
                </span>
              )}
              {!newsRun && !newsRunning && (
                <span className="block text-xs text-muted mt-1 font-normal">Not run yet. Auto-runs every Sunday at 06:00.</span>
              )}
            </button>
            <button
              onClick={runAccessibilityIngest}
              disabled={a11yRunning}
              data-testid="run-accessibility-ingest"
              className="w-full text-left block bg-admin border border-charcoal/10 px-5 py-4 rounded-xl font-semibold hover:border-charcoal/30 transition-colors disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-2">
                <Accessibility size={16}/>
                {a11yRunning ? 'Pulling accessibility data…' : 'Refresh accessibility data from open sources →'}
              </span>
              {a11yRun && !a11yRunning ? (
                <span className="block text-xs text-muted mt-1 font-normal">
                  Last run {new Date(a11yRun.ran_at).toLocaleDateString('en-GB')} ·
                  {' '}{a11yRun.osm_updated} OpenStreetMap ·
                  {' '}{a11yRun.cp_updated} Changing Places ·
                  {' '}{a11yRun.euans_updated} Euan&apos;s Guide ·
                  {' '}{a11yRun.accessable_linked} AccessAble links.
                  Safe to re-run — venue-confirmed data is never overwritten.
                </span>
              ) : (
                <span className="block text-xs text-muted mt-1 font-normal">Pulls from OpenStreetMap, the Changing Places registry, Euan&apos;s Guide, and auto-generates AccessAble search links for venues that don&apos;t have a direct guide URL. Manual only — no scheduled run.</span>
              )}
            </button>
            <AccessableBulkLink />
            <EuansAmbiguousReview />
            {/* Quarterly prize draw */}
            {competition && (
              <div className="block bg-admin border border-charcoal/10 px-5 py-4 rounded-xl" data-testid="competition-tile">
                <div className="flex items-center gap-2 font-semibold">
                  <Gift size={16}/> Quarterly prize draw — {competition.quarter}
                </div>
                {competition.drawn ? (
                  <>
                    <div className="text-xs text-muted mt-1 font-normal">
                      <Check size={11} className="inline mr-1 text-forest"/>
                      Winner: <strong className="text-charcoal">{competition.drawn.winner_email}</strong>{competition.drawn.winner_name ? ` (${competition.drawn.winner_name})` : ''}
                      {' '}· entries: {competition.drawn.entry_count}
                      {' '}· drawn {new Date(competition.drawn.drawn_at).toLocaleDateString('en-GB')}
                    </div>
                    <button
                      onClick={redrawCompetitionWinner}
                      data-testid="redraw-competition"
                      className="mt-2 text-xs text-heritage hover:underline font-semibold"
                    >
                      Pick another winner (audit-logged) →
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-xs text-muted mt-1 font-normal">
                      {competition.entries} opted-in subscriber{competition.entries === 1 ? '' : 's'} so far this quarter.
                      {competition.last_draw && (
                        <> Previous winner: {competition.last_draw.winner_email}.</>
                      )}
                    </div>
                    <button
                      onClick={drawCompetitionWinner}
                      disabled={competition.entries === 0}
                      data-testid="draw-competition"
                      className="mt-2 inline-flex items-center gap-1.5 bg-mustard text-charcoal text-sm font-semibold px-3 py-1.5 rounded-full hover:bg-mustard/90 disabled:opacity-50"
                    >
                      <Gift size={13}/> Pick winner
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="bg-charcoal text-base rounded-2xl p-8">
          <div className="label text-mustard mb-2">Did you know?</div>
          <h3 className="font-display text-2xl mb-4">Featured members appear first, everywhere.</h3>
          <p className="text-base/80 mb-6">Mark any attraction as a featured member (Gold / Silver / Bronze) and they automatically get badge placement on the homepage, the explore grid, and town pages — plus a marquee strip across every page.</p>
          <Link to="/admin/members" className="inline-flex items-center gap-2 text-mustard font-semibold">Manage members <ArrowUpRight size={16}/></Link>
        </div>
      </div>
    </div>
  );
}
